import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from '../store/hooks';
import { testimonials } from '../store/data';

export default function Testimonials() {
  const { ref, isInView } = useInView(0.1);
  const [active, setActive] = useState(0);

  return (
    <section id="testimonials" ref={ref} className="py-28 sm:py-36 relative">
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-xs font-body font-semibold tracking-[0.25em] uppercase text-[#c8ff00] mb-4">
            Client Reviews
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
            Trusted by <span className="text-gradient italic">ambitious</span> brands
          </h2>
        </motion.div>

        {/* Featured testimonial */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="max-w-3xl mx-auto mb-12"
        >
          <div className="glass-card rounded-2xl p-8 sm:p-12 text-center glow-accent">
            {/* Quote icon */}
            <svg className="mx-auto mb-6 text-[#c8ff00]/30" width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11 7.5a5.5 5.5 0 0 0-11 0v.5c0 .28.22.5.5.5H3a.5.5 0 0 0 .5-.5V7.5A2.5 2.5 0 0 1 6 5h.5A.5.5 0 0 0 7 4.5v-2a.5.5 0 0 0-.5-.5H6a5.5 5.5 0 0 0-5.5 5.5V15a3 3 0 0 0 3 3h4a3 3 0 0 0 3-3V7.5zm13 0a5.5 5.5 0 0 0-11 0v.5c0 .28.22.5.5.5H16a.5.5 0 0 0 .5-.5V7.5A2.5 2.5 0 0 1 19 5h.5a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5H19a5.5 5.5 0 0 0-5.5 5.5V15a3 3 0 0 0 3 3h4a3 3 0 0 0 3-3V7.5z" />
            </svg>

            <p className="font-body text-lg sm:text-xl text-surface-200 leading-relaxed mb-8 italic">
              "{testimonials[active].quote}"
            </p>

            {/* Avatar & info */}
            <div className="flex items-center justify-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#c8ff00] to-[#4ade80] flex items-center justify-center">
                <span className="font-body font-bold text-surface-900 text-sm">{testimonials[active].avatar}</span>
              </div>
              <div className="text-left">
                <div className="font-body font-semibold text-white">{testimonials[active].name}</div>
                <div className="font-body text-sm text-surface-500">
                  {testimonials[active].role}, {testimonials[active].company}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Dots navigation */}
        <div className="flex justify-center gap-3">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                i === active
                  ? 'bg-[#c8ff00] scale-110'
                  : 'bg-surface-700 hover:bg-surface-600'
              }`}
              aria-label={`Testimonial ${i + 1}`}
            />
          ))}
        </div>

        {/* Smaller testimonial cards */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12"
        >
          {testimonials.map((t, i) => (
            <button
              key={t.id}
              onClick={() => setActive(i)}
              className={`text-left glass-card-light rounded-xl p-5 transition-all duration-300 ${
                i === active ? 'border-[#c8ff00]/30 bg-[#c8ff00]/[0.03]' : 'hover:border-white/10'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#c8ff00]/40 to-[#4ade80]/40 flex items-center justify-center">
                  <span className="font-body font-bold text-white text-[10px]">{t.avatar}</span>
                </div>
                <div>
                  <div className="font-body font-semibold text-white text-sm">{t.name}</div>
                  <div className="font-body text-[11px] text-surface-500">{t.company}</div>
                </div>
              </div>
              <p className="font-body text-xs text-surface-500 leading-relaxed line-clamp-3">
                "{t.quote}"
              </p>
            </button>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
